import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  MessageSquare, Users, LogOut, Send, Smile, Image as ImageIcon, 
  Palette, Plus, Search, Mic, Video, Settings, UserPlus, Phone, 
  DollarSign, History, Gift, Radio, Target, Shield, Bell, Layout, 
  Film, ArrowLeft, Clock, Sparkles, Zap, ChevronRight
} from 'lucide-react';
import MobileBottomNav from './MobileBottomNav';
import TikTokFeed from './TikTokFeed';
import BigoLiveGrid from './BigoLiveGrid';
import ChatBubble from './ChatBubble';
import DoodleCanvas from './DoodleCanvas';
import EmojiPicker from './EmojiPicker';
import ProfileSettings from './ProfileSettings';
import GroupChatModal from './GroupChatModal';
import VoiceRecorder from './VoiceRecorder';
import VideoCall from './VideoCall';
import GroupVideoCall from './GroupVideoCall';
import MessageSearch from './MessageSearch';
import IncomingCallModal from './IncomingCallModal';
import CallHistoryPanel from './CallHistoryPanel';
import PricingModal from './PricingModal';
import ReferralProgram from './ReferralProgram';
import AdvertiserDashboard from './AdvertiserDashboard';
import LiveStream from './LiveStream';
import LiveHome from './LiveHome';
import AdminPanel from './AdminPanel';
import NotificationCenter from './NotificationCenter';
import StreamOverlays from './StreamOverlays';
import VODLibrary from './VODLibrary';
import Stories, { StoriesBar, StoryViewer, CreateStoryModal } from './Stories';
import SocialFeed from './SocialFeed';
import { RippleButton, GlowButton, NeonButton } from './ui/AnimatedButtons';
import { SkeletonConversation, SkeletonMessage, LoadingSpinner, LoadingDots } from './ui/LoadingSkeletons';
import { PageTransition, SlideUpFadeIn, StaggerList, StaggerItem, GlowPulse, MorphBackground } from './ui/PageTransitions';
import offlineQueueService from '../services/OfflineQueueService';
import { formatDistanceToNow } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

const FuturisticChatApp = () => {
  const { user, logout } = useAuth();
  
  // Loading states
  const [isLoadingConversations, setIsLoadingConversations] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  
  // Navigation state
  const [activeTab, setActiveTab] = useState('live');
  const [activeSubTab, setActiveSubTab] = useState('home');
  
  // Chat state
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [users, setUsers] = useState([]);
  const [showNewChat, setShowNewChat] = useState(false);
  const [showDoodleCanvas, setShowDoodleCanvas] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showGroupChatModal, setShowGroupChatModal] = useState(false);
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const [showVideoCall, setShowVideoCall] = useState(false);
  const [showGroupVideoCall, setShowGroupVideoCall] = useState(false);
  const [groupVideoRoomName, setGroupVideoRoomName] = useState('');
  const [selectedStream, setSelectedStream] = useState(null);
  
  // Stories state
  const [showCreateStory, setShowCreateStory] = useState(false);
  const [viewingStory, setViewingStory] = useState(null);
  const [storiesFeed, setStoriesFeed] = useState([]);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  
  // App state
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [feedNotificationCount, setFeedNotificationCount] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [incomingCall, setIncomingCall] = useState(null);
  const [typingUsers, setTypingUsers] = useState({});
  const [ws, setWs] = useState(null);
  const [userStatuses, setUserStatuses] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  
  // Hub modals state
  const [showAdvertiser, setShowAdvertiser] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [showReferral, setShowReferral] = useState(false);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);

  // WebSocket ref to avoid closure issues
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  useEffect(() => {
    fetchConversations();
    fetchUsers();
    checkAdminStatus();
    fetchNotificationCount();
    fetchFeedNotificationCount();
    
    // Set up interval to check feed notifications periodically
    const feedNotifInterval = setInterval(fetchFeedNotificationCount, 30000);
    
    // Connect WebSocket
    connectWebSocket();

    return () => {
      // Cleanup WebSocket
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        // Use code 1000 for normal closure
        wsRef.current.close(1000, 'Component unmounting');
        wsRef.current = null;
      }
      clearInterval(feedNotifInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const checkAdminStatus = async () => {
    try {
      const response = await axios.get(`${API}/users/me`);
      setIsAdmin(response.data.is_admin || false);
    } catch (error) {
      console.error('Error checking admin status:', error);
    }
  };

  const fetchFeedNotificationCount = async () => {
    try {
      const response = await axios.get(`${API}/feed/notifications/count`);
      setFeedNotificationCount(response.data.count || 0);
    } catch (error) {
      console.error('Error fetching feed notification count:', error);
    }
  };

  const markFeedAsRead = async () => {
    try {
      await axios.post(`${API}/feed/notifications/mark-read`);
      setFeedNotificationCount(0);
    } catch (error) {
      console.error('Error marking feed as read:', error);
    }
  };

  const fetchNotificationCount = async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread-count`);
      setUnreadNotifications(response.data.count || 0);
    } catch (error) {
      console.error('Error fetching notification count:', error);
    }
  };

  const connectWebSocket = () => {
    if (!user) return;
    
    // Check if already connected or connecting
    if (wsRef.current) {
      const state = wsRef.current.readyState;
      if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) {
        return; // Already connected or connecting
      }
    }
    
    try {
      const websocket = new WebSocket(`${WS_URL}/ws/${user.id}`);
      wsRef.current = websocket;
      
      websocket.onopen = () => {
        console.log('[WS] Connected');
        setWs(websocket);
      };
      
      websocket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleWebSocketMessage(data);
        } catch (e) {
          console.error('[WS] Parse error:', e);
        }
      };
      
      websocket.onclose = (event) => {
        console.log('[WS] Disconnected, code:', event.code);
        setWs(null);
        wsRef.current = null;
        
        // Only reconnect if not intentionally closed and component is still mounted
        if (event.code !== 1000 && event.code !== 1001) {
          reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
        }
      };
      
      websocket.onerror = (error) => {
        console.error('[WS] Error:', error);
      };
    } catch (error) {
      console.error('[WS] Connection error:', error);
    }
  };

  const handleWebSocketMessage = (data) => {
    switch (data.type) {
      case 'new_message':
        if (selectedConversation?.id === data.message.conversation_id) {
          setMessages((prev) => [...prev, data.message]);
        }
        fetchConversations();
        break;
      case 'typing':
        setTypingUsers((prev) => ({
          ...prev,
          [data.conversation_id]: { user_id: data.user_id, user_name: data.user_name }
        }));
        break;
      case 'stop_typing':
        setTypingUsers((prev) => {
          const newState = { ...prev };
          delete newState[data.conversation_id];
          return newState;
        });
        break;
      case 'user_status':
        setUserStatuses((prev) => ({
          ...prev,
          [data.user_id]: data.status
        }));
        break;
      case 'incoming_call':
        setIncomingCall(data);
        break;
      case 'notification':
        fetchNotificationCount();
        break;
      default:
        break;
    }
  };

  const fetchConversations = async () => {
    setIsLoadingConversations(true);
    try {
      const response = await axios.get(`${API}/conversations`);
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setIsLoadingConversations(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data.filter((u) => u.id !== user.id));
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchMessages = async (conversationId) => {
    setIsLoadingMessages(true);
    try {
      const response = await axios.get(`${API}/conversations/${conversationId}/messages`);
      const queuedMessages = offlineQueueService.getQueuedMessages(conversationId);
      const queuedWithMeta = queuedMessages.map(msg => ({
        ...msg,
        created_at: msg.queued_at,
        reactions: [],
        read_by: [user.id],
        _isPending: msg.status === 'queued' || msg.status === 'sending'
      }));
      setMessages([...response.data, ...queuedWithMeta]);
    } catch (error) {
      console.error('Error fetching messages:', error);
      if (!navigator.onLine) {
        const queuedMessages = offlineQueueService.getQueuedMessages(conversationId);
        const queuedWithMeta = queuedMessages.map(msg => ({
          ...msg,
          created_at: msg.queued_at,
          reactions: [],
          read_by: [user.id],
          _isPending: msg.status === 'queued' || msg.status === 'sending'
        }));
        setMessages(queuedWithMeta);
      }
    } finally {
      setIsLoadingMessages(false);
    }
  };

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.id);
      markMessagesAsRead(selectedConversation.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversation?.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const markMessagesAsRead = async (conversationId) => {
    try {
      await axios.post(`${API}/conversations/${conversationId}/read`);
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  const createConversation = async (participantId) => {
    try {
      const response = await axios.post(`${API}/conversations`, {
        participant_ids: [participantId],
        is_group: false
      });
      setConversations([response.data, ...conversations]);
      setSelectedConversation(response.data);
      setShowNewChat(false);
    } catch (error) {
      console.error('Error creating conversation:', error);
    }
  };

  const sendMessage = async (content = null, messageType = 'text', mediaUrl = null, doodleData = null) => {
    if (!selectedConversation || (!content && !mediaUrl && !doodleData)) return;

    const messageData = {
      conversation_id: selectedConversation.id,
      content,
      message_type: messageType,
      media_url: mediaUrl,
      doodle_data: doodleData,
      sender_id: user.id,
      sender_name: user.name,
      sender_avatar: user.avatar
    };

    if (!navigator.onLine) {
      const queuedMessage = await offlineQueueService.queueMessage(messageData);
      const pendingMessage = {
        ...queuedMessage,
        created_at: new Date().toISOString(),
        reactions: [],
        read_by: [user.id],
        _isPending: true
      };
      setMessages((prev) => [...prev, pendingMessage]);
      setMessageText('');
      return;
    }

    try {
      const response = await axios.post(`${API}/messages`, {
        conversation_id: selectedConversation.id,
        content,
        message_type: messageType,
        media_url: mediaUrl,
        doodle_data: doodleData
      });
      setMessages((prev) => [...prev, response.data]);
      setMessageText('');
      fetchConversations();
    } catch (error) {
      console.error('Error sending message:', error);
      if (!navigator.onLine || error.code === 'ERR_NETWORK') {
        const queuedMessage = await offlineQueueService.queueMessage(messageData);
        const pendingMessage = {
          ...queuedMessage,
          created_at: new Date().toISOString(),
          reactions: [],
          read_by: [user.id],
          _isPending: true
        };
        setMessages((prev) => [...prev, pendingMessage]);
        setMessageText('');
      }
    }
  };

  useEffect(() => {
    const handleMessageSynced = (event) => {
      const { tempId, serverMessage } = event.detail;
      setMessages((prev) => prev.map(msg => 
        msg.id === tempId 
          ? { ...serverMessage, _isPending: false }
          : msg
      ));
    };
    window.addEventListener('messageSynced', handleMessageSynced);
    return () => window.removeEventListener('messageSynced', handleMessageSynced);
  }, []);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (messageText.trim()) {
      sendMessage(messageText.trim());
    }
  };

  const handleTyping = (isTyping) => {
    if (!ws || !selectedConversation) return;
    
    if (isTyping) {
      ws.send(JSON.stringify({
        type: 'typing',
        conversation_id: selectedConversation.id,
        user_id: user.id,
        user_name: user.name
      }));
      
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        ws.send(JSON.stringify({
          type: 'stop_typing',
          conversation_id: selectedConversation.id,
          user_id: user.id
        }));
      }, 2000);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/upload`, formData);
      const fileType = file.type.startsWith('image/') ? 'image' : 'file';
      sendMessage(file.name, fileType, response.data.url);
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  };

  const handleReaction = async (messageId, emoji, isFloating) => {
    try {
      await axios.post(`${API}/messages/${messageId}/react`, { emoji, floating: isFloating });
      fetchMessages(selectedConversation.id);
    } catch (error) {
      console.error('Error adding reaction:', error);
    }
  };

  const getOtherParticipant = (conv) => {
    if (conv.is_group) return null;
    return conv.participants?.find((p) => p.id !== user.id);
  };

  const filteredConversations = conversations.filter((conv) => {
    if (!searchQuery) return true;
    const otherUser = getOtherParticipant(conv);
    const name = conv.is_group ? conv.name : otherUser?.name || '';
    return name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Render content based on active tab and subtab
  const renderContent = () => {
    // Full screen modals
    if (showVideoCall) {
      return <VideoCall conversation={selectedConversation} onClose={() => setShowVideoCall(false)} ws={ws} />;
    }

    // Tab-based content
    switch (activeTab) {
      case 'live':
        return renderLiveContent();
      case 'feed':
        return renderFeedContent();
      case 'chat':
        return renderChatContent();
      case 'video':
        return renderVideoContent();
      case 'profile':
        return renderProfileContent();
      default:
        return renderLiveContent();
    }
  };

  const renderLiveContent = () => {
    switch (activeSubTab) {
      case 'watching':
      case 'streaming':
        return <LiveStream onClose={() => setActiveSubTab('home')} />;
      case 'vods':
        return <VODLibrary onClose={() => setActiveSubTab('home')} />;
      case 'overlays':
        return <StreamOverlays onClose={() => setActiveSubTab('home')} />;
      case 'home':
      default:
        return (
          <LiveHome 
            onWatchStream={(stream) => {
              setActiveSubTab('watching');
              setSelectedStream(stream);
            }}
            onGoLive={() => setActiveSubTab('streaming')}
          />
        );
    }
  };

  const renderFeedContent = () => {
    // Mark feed as read when user visits
    if (feedNotificationCount > 0) {
      markFeedAsRead();
    }
    return <SocialFeed initialTab={activeSubTab} />;
  };

  const renderChatContent = () => {
    switch (activeSubTab) {
      case 'messages':
        return renderMessagesView();
      case 'groups':
        return renderGroupsView();
      case 'search':
        return <MessageSearch onClose={() => setActiveSubTab('messages')} />;
      default:
        return renderMessagesView();
    }
  };

  const renderVideoContent = () => {
    switch (activeSubTab) {
      case 'call':
        return renderVideoCallPrompt();
      case 'voice':
        return <VoiceRecorder onSend={(audioUrl) => { sendMessage('Voice note', 'voice', audioUrl); setActiveSubTab('messages'); setActiveTab('chat'); }} onClose={() => setActiveSubTab('call')} />;
      case 'history':
        return <CallHistoryPanel onClose={() => setActiveSubTab('call')} />;
      default:
        return renderVideoCallPrompt();
    }
  };

  const renderProfileContent = () => {
    switch (activeSubTab) {
      case 'settings':
        return <ProfileSettings onClose={() => {}} />;
      case 'hub':
        return renderHubContent();
      case 'notifications':
        return <NotificationCenter onClose={() => setActiveSubTab('settings')} />;
      default:
        return <ProfileSettings onClose={() => {}} />;
    }
  };

  const renderHubContent = () => {
    return (
      <div className="flex-1 p-6 bg-[#030305] overflow-y-auto">
        <h2 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>Dashboard Hub</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <motion.button
            whileHover={{ scale: 1.02 }}
            onClick={() => setShowAdvertiser(true)}
            className="p-6 rounded-2xl bg-[#08080C] border border-white/5 hover:border-[#00F0FF]/20 text-left transition-all group"
          >
            <Target className="w-8 h-8 text-[#00F0FF] mb-3" />
            <h3 className="text-lg font-semibold text-white mb-1">Advertiser Hub</h3>
            <p className="text-sm text-gray-500">Manage campaigns and analytics</p>
          </motion.button>
          {isAdmin && (
            <motion.button
              whileHover={{ scale: 1.02 }}
              onClick={() => setShowAdmin(true)}
              className="p-6 rounded-2xl bg-[#08080C] border border-white/5 hover:border-[#FFE800]/20 text-left transition-all group"
            >
              <Shield className="w-8 h-8 text-[#FFE800] mb-3" />
              <h3 className="text-lg font-semibold text-white mb-1">Admin Panel</h3>
              <p className="text-sm text-gray-500">Platform moderation tools</p>
            </motion.button>
          )}
          <motion.button
            whileHover={{ scale: 1.02 }}
            onClick={() => setShowPricing(true)}
            className="p-6 rounded-2xl bg-[#08080C] border border-white/5 hover:border-[#00FF66]/20 text-left transition-all group"
          >
            <DollarSign className="w-8 h-8 text-[#00FF66] mb-3" />
            <h3 className="text-lg font-semibold text-white mb-1">Pricing Plans</h3>
            <p className="text-sm text-gray-500">Upgrade your subscription</p>
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            onClick={() => setShowReferral(true)}
            className="p-6 rounded-2xl bg-[#08080C] border border-white/5 hover:border-[#FF2A2A]/20 text-left transition-all group"
          >
            <Gift className="w-8 h-8 text-[#FF2A2A] mb-3" />
            <h3 className="text-lg font-semibold text-white mb-1">Referral Program</h3>
            <p className="text-sm text-gray-500">Earn rewards for inviting friends</p>
          </motion.button>
        </div>
      </div>
    );
  };

  const renderMessagesView = () => (
    <div className="flex flex-col h-full">
      {/* Stories Bar */}
      <StoriesBar 
        onStoryClick={(userStory) => {
          const index = storiesFeed.findIndex(s => s.user_id === userStory.user_id);
          setCurrentStoryIndex(index >= 0 ? index : 0);
          setViewingStory(userStory);
        }}
        onCreateStory={() => setShowCreateStory(true)}
      />
      
      <div className="flex flex-1 min-h-0">
      {/* Conversations List */}
      <AnimatePresence>
        {(!selectedConversation || window.innerWidth >= 768) && (
          <motion.div 
            initial={{ x: -300, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -300, opacity: 0 }}
            className="w-full md:w-80 lg:w-96 border-r border-white/5 flex flex-col bg-[#030305]"
          >
            {/* Header */}
            <div className="p-4 border-b border-white/5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#00F0FF] to-[#00F0FF]/50 rounded-xl flex items-center justify-center shadow-[0_0_20px_rgba(0,240,255,0.3)]">
                    <MessageSquare className="w-5 h-5 text-black" />
                  </div>
                  <div>
                    <h1 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Messages</h1>
                    <p className="text-xs text-gray-500">{conversations.length} conversations</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowGroupChatModal(true)}
                    className="p-2 rounded-xl bg-white/5 hover:bg-white/10 text-gray-400 hover:text-[#00F0FF] transition-all"
                    data-testid="create-group-btn"
                  >
                    <Users className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setShowNewChat(!showNewChat)}
                    className="p-2 rounded-xl bg-[#00F0FF] text-black hover:shadow-[0_0_20px_rgba(0,240,255,0.4)] transition-all"
                    data-testid="new-chat-btn"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="text"
                  placeholder="Search conversations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-[#08080C] border border-white/5 rounded-xl text-white placeholder-gray-500 focus:border-[#00F0FF]/50 focus:outline-none focus:ring-1 focus:ring-[#00F0FF]/20 transition-all"
                  data-testid="search-conversations"
                />
              </div>
            </div>

            {/* New Chat User List */}
            <AnimatePresence>
              {showNewChat && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="border-b border-white/5 overflow-hidden"
                >
                  <div className="p-3 max-h-48 overflow-y-auto">
                    <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Start a new chat</p>
                    {users.map((u) => (
                      <button
                        key={u.id}
                        onClick={() => createConversation(u.id)}
                        className="w-full p-3 rounded-xl hover:bg-white/5 flex items-center gap-3 transition-all group"
                        data-testid={`new-chat-user-${u.id}`}
                      >
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00F0FF]/20 to-[#FFE800]/20 flex items-center justify-center text-white font-bold border border-white/10">
                          {u.name?.[0]?.toUpperCase() || 'U'}
                        </div>
                        <span className="text-gray-300 group-hover:text-white transition-colors">{u.name}</span>
                        <ChevronRight className="w-4 h-4 text-gray-600 ml-auto group-hover:text-[#00F0FF] transition-colors" />
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Conversations */}
            <div className="flex-1 overflow-y-auto p-3 space-y-1 scrollbar-dark">
              {isLoadingConversations ? (
                // Loading skeletons
                <StaggerList>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <StaggerItem key={i}>
                      <SkeletonConversation />
                    </StaggerItem>
                  ))}
                </StaggerList>
              ) : (
                <StaggerList>
                  {filteredConversations.map((conv) => {
                    const otherUser = getOtherParticipant(conv);
                    const isSelected = selectedConversation?.id === conv.id;
                    
                    return (
                      <StaggerItem key={conv.id}>
                        <motion.button
                          whileHover={{ scale: 1.01, x: 4 }}
                          whileTap={{ scale: 0.99 }}
                          onClick={() => setSelectedConversation(conv)}
                          data-testid={`conversation-item-${conv.id}`}
                          className={`w-full p-3 rounded-xl flex items-center gap-3 transition-all ${
                            isSelected 
                              ? 'bg-[#00F0FF]/10 border border-[#00F0FF]/20 shadow-[0_0_15px_rgba(0,240,255,0.1)]' 
                              : 'hover:bg-white/5 border border-transparent'
                          }`}
                        >
                          <motion.div 
                            className={`w-12 h-12 rounded-full flex items-center justify-center font-bold ${
                              isSelected 
                                ? 'bg-gradient-to-br from-[#00F0FF] to-[#00F0FF]/50 text-black' 
                                : 'bg-[#0F0F16] text-gray-400 border border-white/5'
                            }`}
                            whileHover={{ rotate: [0, -5, 5, 0] }}
                            transition={{ duration: 0.3 }}
                          >
                            {conv.is_group ? <Users className="w-5 h-5" /> : (otherUser?.name?.[0]?.toUpperCase() || 'U')}
                          </motion.div>
                          <div className="flex-1 text-left min-w-0">
                            <p className={`font-semibold truncate ${isSelected ? 'text-[#00F0FF]' : 'text-white'}`}>
                              {conv.is_group ? conv.name : otherUser?.name || 'User'}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {conv.last_message?.content || 'Start a conversation'}
                            </p>
                          </div>
                          {conv.unread_count > 0 && (
                            <motion.span 
                              className="min-w-[20px] h-5 px-1.5 bg-[#00F0FF] text-black text-xs font-bold rounded-full flex items-center justify-center"
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{ type: 'spring', damping: 15 }}
                            >
                              {conv.unread_count}
                            </motion.span>
                          )}
                        </motion.button>
                      </StaggerItem>
                    );
                  })}
                </StaggerList>
              )}
            </div>

            {/* Logout */}
            <div className="p-4 border-t border-white/5">
              <button
                onClick={logout}
                className="w-full p-3 rounded-xl bg-[#FF2A2A]/10 text-[#FF2A2A] hover:bg-[#FF2A2A]/20 flex items-center justify-center gap-2 transition-all"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
                <span className="font-medium">Sign Out</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat Area */}
      {selectedConversation ? (
        <div className="flex-1 flex flex-col bg-[#030305]">
          {/* Chat Header */}
          <div className="p-4 border-b border-white/5 backdrop-blur-xl bg-[#030305]/80">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setSelectedConversation(null)}
                  className="p-2 rounded-xl hover:bg-white/5 md:hidden"
                  data-testid="back-to-list"
                >
                  <ArrowLeft className="w-5 h-5 text-gray-400" />
                </button>
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00F0FF]/20 to-[#FFE800]/20 flex items-center justify-center text-white font-bold border border-white/10">
                  {selectedConversation.is_group ? <Users className="w-5 h-5" /> : 'U'}
                </div>
                <div>
                  <p className="font-semibold text-white">
                    {selectedConversation.is_group ? selectedConversation.name : getOtherParticipant(selectedConversation)?.name || 'User'}
                  </p>
                  <p className="text-xs text-[#00FF66]">Online</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowVideoCall(true)}
                  className="p-2 rounded-xl bg-white/5 hover:bg-[#00F0FF]/20 text-gray-400 hover:text-[#00F0FF] transition-all"
                  data-testid="start-video-call"
                >
                  <Video className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setShowVoiceRecorder(true)}
                  className="p-2 rounded-xl bg-white/5 hover:bg-[#00F0FF]/20 text-gray-400 hover:text-[#00F0FF] transition-all"
                  data-testid="start-voice-note"
                >
                  <Mic className="w-5 h-5" />
                </button>
              </div>
            </div>
            {typingUsers[selectedConversation.id] && (
              <p className="text-xs text-[#FFE800] mt-2 flex items-center gap-1">
                <LoadingDots color="#FFE800" />
                <span className="ml-1">{typingUsers[selectedConversation.id].user_name} is typing...</span>
              </p>
            )}
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-dark">
            {isLoadingMessages ? (
              // Loading skeletons
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <SlideUpFadeIn key={i} delay={i * 0.05}>
                    <SkeletonMessage isOwn={i % 2 === 0} />
                  </SlideUpFadeIn>
                ))}
              </div>
            ) : (
              <AnimatePresence>
                {messages.map((msg, index) => (
                  <motion.div
                    key={msg.id}
                    initial={{ opacity: 0, y: 20, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ 
                      delay: index > messages.length - 3 ? 0 : 0,
                      type: 'spring',
                      damping: 25
                    }}
                  >
                    <ChatBubble
                      message={msg}
                      isOwn={msg.sender_id === user.id}
                      onReaction={handleReaction}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Message Input */}
          <div className="p-4 border-t border-white/5 backdrop-blur-xl bg-[#030305]/80">
            <form onSubmit={handleSendMessage} className="flex items-center gap-3">
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  className="p-3 rounded-xl bg-[#08080C] hover:bg-[#0F0F16] text-gray-400 hover:text-[#FFE800] transition-all"
                  data-testid="emoji-btn"
                >
                  <Smile className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="p-3 rounded-xl bg-[#08080C] hover:bg-[#0F0F16] text-gray-400 hover:text-[#00F0FF] transition-all"
                  data-testid="upload-btn"
                >
                  <ImageIcon className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={() => setShowDoodleCanvas(true)}
                  className="p-3 rounded-xl bg-[#08080C] hover:bg-[#0F0F16] text-gray-400 hover:text-[#00FF66] transition-all hidden sm:block"
                  data-testid="doodle-btn"
                >
                  <Palette className="w-5 h-5" />
                </button>
              </div>
              <input
                type="text"
                value={messageText}
                onChange={(e) => {
                  setMessageText(e.target.value);
                  handleTyping(true);
                }}
                placeholder="Type a message..."
                className="flex-1 px-5 py-3 bg-[#08080C] border border-white/5 rounded-xl text-white placeholder-gray-500 focus:border-[#00F0FF]/50 focus:outline-none focus:ring-1 focus:ring-[#00F0FF]/20 transition-all focus-ring-cyan"
                data-testid="message-input"
              />
              <RippleButton
                type="submit"
                disabled={!messageText.trim()}
                variant="primary"
                className="p-3"
                data-testid="send-btn"
              >
                <Send className="w-5 h-5" />
              </RippleButton>
            </form>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileUpload}
              className="hidden"
              accept="image/*,video/*,audio/*,.pdf,.doc,.docx"
            />
          </div>

          {/* Emoji Picker */}
          <AnimatePresence>
            {showEmojiPicker && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="absolute bottom-24 left-4"
              >
                <EmojiPicker onSelect={(emoji) => { setMessageText(prev => prev + emoji); setShowEmojiPicker(false); }} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ) : (
        <div className="hidden md:flex flex-1 flex-col items-center justify-center bg-[#030305]">
          <div className="w-24 h-24 bg-gradient-to-br from-[#00F0FF]/20 to-[#FFE800]/20 rounded-3xl flex items-center justify-center mb-6 shadow-[0_0_40px_rgba(0,240,255,0.1)]">
            <MessageSquare className="w-12 h-12 text-[#00F0FF]" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Welcome to Pulsate</h2>
          <p className="text-gray-500 text-center max-w-md">Select a conversation to start messaging, or create a new one to connect with others.</p>
        </div>
      )}

      {/* Modals */}
      {showDoodleCanvas && (
        <DoodleCanvas
          onSend={(doodleData) => { sendMessage(null, 'doodle', null, doodleData); setShowDoodleCanvas(false); }}
          onClose={() => setShowDoodleCanvas(false)}
        />
      )}
      {showGroupChatModal && (
        <GroupChatModal
          users={users}
          onClose={() => setShowGroupChatModal(false)}
          onGroupCreated={(group) => { setConversations([group, ...conversations]); setSelectedConversation(group); setShowGroupChatModal(false); }}
        />
      )}
      {showVoiceRecorder && (
        <VoiceRecorder
          onSend={(audioUrl) => { sendMessage('Voice note', 'voice', audioUrl); setShowVoiceRecorder(false); }}
          onClose={() => setShowVoiceRecorder(false)}
        />
      )}
    </div>
    </div>
  );

  const renderGroupsView = () => (
    <div className="flex-1 p-6 bg-[#030305]">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Group Chats</h2>
        <button
          onClick={() => setShowGroupChatModal(true)}
          className="px-4 py-2 rounded-xl bg-[#00F0FF] text-black font-semibold hover:shadow-[0_0_20px_rgba(0,240,255,0.4)] transition-all flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          New Group
        </button>
      </div>
      <div className="grid gap-4">
        {conversations.filter(c => c.is_group).map((group) => (
          <motion.button
            key={group.id}
            whileHover={{ scale: 1.01 }}
            onClick={() => { setSelectedConversation(group); setActiveSubTab('messages'); }}
            className="p-4 rounded-2xl bg-[#08080C] border border-white/5 hover:border-[#00F0FF]/20 flex items-center gap-4 transition-all text-left"
          >
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00F0FF]/20 to-[#FFE800]/20 flex items-center justify-center border border-white/10">
              <Users className="w-6 h-6 text-[#00F0FF]" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-white">{group.name}</p>
              <p className="text-sm text-gray-500">{group.participants?.length || 0} members</p>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </motion.button>
        ))}
        {conversations.filter(c => c.is_group).length === 0 && (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-gray-700 mx-auto mb-4" />
            <p className="text-gray-500">No group chats yet</p>
            <button
              onClick={() => setShowGroupChatModal(true)}
              className="mt-4 text-[#00F0FF] hover:underline"
            >
              Create your first group
            </button>
          </div>
        )}
      </div>
      {showGroupChatModal && (
        <GroupChatModal
          users={users}
          onClose={() => setShowGroupChatModal(false)}
          onGroupCreated={(group) => { setConversations([group, ...conversations]); setSelectedConversation(group); setShowGroupChatModal(false); setActiveSubTab('messages'); }}
        />
      )}
    </div>
  );

  const startGroupVideoCall = (roomName = null) => {
    const room = roomName || `room-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setGroupVideoRoomName(room);
    setShowGroupVideoCall(true);
  };

  const renderVideoCallPrompt = () => (
    <div className="flex-1 flex flex-col items-center justify-center p-6 bg-[#030305]">
      <div className="w-32 h-32 bg-gradient-to-br from-[#00F0FF]/20 to-[#FFE800]/20 rounded-3xl flex items-center justify-center mb-8 shadow-[0_0_60px_rgba(0,240,255,0.15)]">
        <Video className="w-16 h-16 text-[#00F0FF]" />
      </div>
      <h2 className="text-3xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Video Calls</h2>
      <p className="text-gray-500 text-center max-w-md mb-8">Start a 1:1 video call or create a group video room for up to 10+ participants.</p>
      
      <div className="flex flex-col sm:flex-row gap-4">
        <button
          onClick={() => { setActiveTab('chat'); setActiveSubTab('messages'); }}
          className="px-8 py-4 rounded-full bg-[#0F0F16] text-white font-semibold border border-white/10 hover:border-[#00F0FF]/30 transition-all flex items-center gap-3"
        >
          <Phone className="w-5 h-5" />
          1:1 Video Call
        </button>
        
        <button
          onClick={() => startGroupVideoCall()}
          className="px-8 py-4 rounded-full bg-[#00F0FF] text-black font-semibold hover:shadow-[0_0_30px_rgba(0,240,255,0.4)] transition-all flex items-center gap-3"
          data-testid="start-group-call-btn"
        >
          <Users className="w-5 h-5" />
          Group Video Call
        </button>
      </div>
      
      <p className="text-gray-600 text-sm mt-6">Group calls powered by LiveKit WebRTC SFU</p>
    </div>
  );

  return (
    <div className="h-screen flex flex-col bg-[#030305] overflow-hidden" style={{ fontFamily: 'Manrope, sans-serif' }}>
      {/* Main Content Area */}
      <div className="flex-1 overflow-hidden pb-20 md:pb-24">
        {renderContent()}
      </div>

      {/* Bottom Navigation - Mobile App Style */}
      <MobileBottomNav
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        activeSubTab={activeSubTab}
        setActiveSubTab={setActiveSubTab}
        unreadNotifications={unreadNotifications}
        feedNotificationCount={feedNotificationCount}
        messageCount={conversations.filter(c => c.unread_count > 0).length}
        onGoLive={() => {
          setActiveTab('live');
          setActiveSubTab('streaming');
        }}
      />

      {/* Incoming Call Modal */}
      {incomingCall && (
        <IncomingCallModal
          call={incomingCall}
          onAccept={() => { setShowVideoCall(true); setIncomingCall(null); }}
          onDecline={() => setIncomingCall(null)}
        />
      )}

      {/* Story Viewer */}
      <AnimatePresence>
        {viewingStory && (
          <StoryViewer
            userStory={viewingStory}
            onClose={() => setViewingStory(null)}
            onNext={() => {
              if (currentStoryIndex < storiesFeed.length - 1) {
                setCurrentStoryIndex(prev => prev + 1);
                setViewingStory(storiesFeed[currentStoryIndex + 1]);
              } else {
                setViewingStory(null);
              }
            }}
            onPrev={() => {
              if (currentStoryIndex > 0) {
                setCurrentStoryIndex(prev => prev - 1);
                setViewingStory(storiesFeed[currentStoryIndex - 1]);
              }
            }}
            hasNext={currentStoryIndex < storiesFeed.length - 1}
            hasPrev={currentStoryIndex > 0}
          />
        )}
      </AnimatePresence>

      {/* Create Story Modal */}
      <AnimatePresence>
        {showCreateStory && (
          <CreateStoryModal
            onClose={() => setShowCreateStory(false)}
            onCreated={() => {
              setShowCreateStory(false);
              // Refresh stories feed
            }}
          />
        )}
      </AnimatePresence>

      {/* Hub Modals */}
      {showAdvertiser && (
        <div className="fixed inset-0 bg-black/80 z-50 overflow-y-auto">
          <AdvertiserDashboard onClose={() => setShowAdvertiser(false)} />
        </div>
      )}
      {showAdmin && isAdmin && (
        <div className="fixed inset-0 bg-black/80 z-50 overflow-y-auto">
          <AdminPanel onClose={() => setShowAdmin(false)} />
        </div>
      )}
      {showPricing && (
        <div className="fixed inset-0 bg-black/80 z-50 overflow-y-auto">
          <PricingModal onClose={() => setShowPricing(false)} />
        </div>
      )}
      {showReferral && (
        <div className="fixed inset-0 bg-black/80 z-50 overflow-y-auto">
          <ReferralProgram onClose={() => setShowReferral(false)} />
        </div>
      )}

      {/* Group Video Call */}
      {showGroupVideoCall && (
        <GroupVideoCall
          roomName={groupVideoRoomName}
          onLeave={() => {
            setShowGroupVideoCall(false);
            setGroupVideoRoomName('');
          }}
        />
      )}
    </div>
  );
};

export default FuturisticChatApp;
